import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  PencilIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../Common/LoadingSpinner';

const EditCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [course, setCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseCode: '',
    credits: 3,
    maxStudents: 30,
    fees: 0,
    category: '',
    level: 'Beginner',
    prerequisites: [''],
    banner: ''
  });

  const categories = [
    'Computer Science', 'Mathematics', 'Physics', 'Chemistry', 
    'Biology', 'English', 'History', 'Arts', 'Business', 'Other'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`/api/courses/${id}`);
      const courseData = response.data;
      setCourse(courseData);
      
      // Populate form with course data
      setFormData({
        title: courseData.title || '',
        description: courseData.description || '',
        courseCode: courseData.courseCode || '',
        credits: courseData.credits || 3,
        maxStudents: courseData.maxStudents || 30,
        fees: courseData.fees || 0,
        category: courseData.category || '',
        level: courseData.level || 'Beginner',
        prerequisites: courseData.prerequisites && courseData.prerequisites.length > 0 
          ? courseData.prerequisites 
          : [''],
        banner: courseData.banner || ''
      });
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course details');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handlePrerequisiteChange = (index, value) => {
    const newPrerequisites = [...formData.prerequisites];
    newPrerequisites[index] = value;
    setFormData(prev => ({ ...prev, prerequisites: newPrerequisites }));
  };

  const addPrerequisite = () => {
    setFormData(prev => ({
      ...prev,
      prerequisites: [...prev.prerequisites, '']
    }));
  };

  const removePrerequisite = (index) => {
    const newPrerequisites = formData.prerequisites.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      prerequisites: newPrerequisites.length > 0 ? newPrerequisites : ['']
    }));
  };

  // Banner upload handler
  const handleBannerUpload = async (file) => {
    if (!file) return;

    // Validate file size (max 5MB for banners)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Banner file size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed for banners');
      return;
    }

    try {
      setBannerUploading(true);

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post('/api/upload/banner', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update form data with the uploaded banner URL
      setFormData(prev => ({
        ...prev,
        banner: response.data.url
      }));

      toast.success('Banner uploaded successfully!');
    } catch (error) {
      console.error('Banner upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload banner');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);

      // Filter out empty prerequisites
      const cleanPrerequisites = formData.prerequisites.filter(p => p.trim() !== '');

      const courseData = {
        ...formData,
        prerequisites: cleanPrerequisites
      };

      await axios.put(`/api/courses/${id}`, courseData);
      
      toast.success('Course updated successfully!');
      navigate(`/courses/${id}`);
    } catch (error) {
      console.error('Update error:', error);
      const message = error.response?.data?.message || 'Failed to update course';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Course not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Course</h1>
        <p className="mt-2 text-gray-600">Update course details and information</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="input"
                placeholder="e.g., Introduction to Computer Science"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Code *
              </label>
              <input
                type="text"
                name="courseCode"
                required
                value={formData.courseCode}
                onChange={handleChange}
                className="input"
                placeholder="e.g., CS101"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                required
                value={formData.category}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Level *
              </label>
              <select
                name="level"
                required
                value={formData.level}
                onChange={handleChange}
                className="input"
              >
                {levels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credits *
              </label>
              <input
                type="number"
                name="credits"
                required
                min="1"
                max="10"
                value={formData.credits}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Students *
              </label>
              <input
                type="number"
                name="maxStudents"
                required
                min="1"
                value={formData.maxStudents}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Fees (₹) *
              </label>
              <input
                type="number"
                name="fees"
                required
                min="0"
                value={formData.fees}
                onChange={handleChange}
                className="input"
              />
            </div>
          </div>

          {/* Banner Image Upload */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Banner Image
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <span>Upload banner image</span>
                    <input
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleBannerUpload(file);
                        }
                      }}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF, WEBP up to 5MB
                </p>
                {bannerUploading ? (
                  <div className="text-sm text-blue-600">
                    <div className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                    Uploading banner...
                  </div>
                ) : formData.banner ? (
                  <p className="text-sm text-green-600 font-medium">✓ Banner uploaded</p>
                ) : (
                  <p className="text-sm text-gray-500">No banner uploaded</p>
                )}
              </div>
            </div>
            {formData.banner && (
              <div className="mt-3">
                <img
                  src={formData.banner}
                  alt="Course Banner Preview"
                  className="h-40 w-full object-cover rounded-lg border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              required
              rows="5"
              value={formData.description}
              onChange={handleChange}
              className="input"
              placeholder="Provide a detailed description of the course..."
            />
          </div>
        </div>

        {/* Prerequisites */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Prerequisites</h2>
          <div className="space-y-3">
            {formData.prerequisites.map((prereq, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={prereq}
                  onChange={(e) => handlePrerequisiteChange(index, e.target.value)}
                  className="input flex-1"
                  placeholder="e.g., Basic programming knowledge"
                />
                {formData.prerequisites.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePrerequisite(index)}
                    className="btn btn-danger px-3 py-2"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addPrerequisite}
              className="btn btn-secondary"
            >
              + Add Prerequisite
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(`/courses/${id}`)}
            className="btn btn-secondary"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary flex items-center"
            disabled={saving}
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            {saving ? 'Updating...' : 'Update Course'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCourse;
